/**
 * Locale for en-US (English)
 */
export default {
    Shared: {
        /** Click to confirm an action */
        ok: "Ok",
        /** Click to cancel an action */
        cancel: "Cancel",
        /** Click to close a dialog or a panel */
        close: "Close",
        /** Conjunction for series of entities */
        conjunction: ", ",
        /** Conjunction for the last item in a series of entities */
        conjunctionLast: " and ",
        /**
         * Format for presenting a percentage.
         * {{number}} is the number of the percentage.
         * For example: 50%
         */
        percentage: "{{number}}%",
        /* Alert message in loading state */
        loading: "Loading",
    },
    Autocomplete: {
        /** Click to select all items of a list */
        selectAll: "All",
        /**
         * Click to add a user-specified label into a list.
         * {{label}} is the content of the label.
         * For example (in a color platte): Add “Red”
         */
        addLabel: "Add “{{value}}”",
        /**
         * Click to remove a label from a list.
         * {{label}} is the content of the label.
         * For example (in a color palette): Remove “Red”
         */
        removeLabel: "Remove “{{label}}”",
        /**
         * The label for hidden items in a truncated list.
         * {{more}} is the number of hidden items.
         * For example (in the end of a truncated list with 3 hidden items): and 3 more
         * Translating to "+ {{more}}" is acceptable if there is no equivalent expression for specified language.
         */
        limitTags: "and {{more}} more",
        /** The label for loading state */
        loading: "Loading…",
        /** The label for an empty option list */
        noOptions: "No options",
        /** Click to clear all selected items */
        clear: "Clear",
        /**
         * Label for the selected item count of a list.
         * {{count}} is the number of items in the list.
         * For example (when the user selected 2 items): 2 selected
         */
        selectedCount: "{{count}} selected",
    },
    Avatar: {
        /** A general placeholder for a user's avatar (for screen reader / braille display only) */
        defaultProfileAvatar: "user profile avatar",
        /**
         * A placeholder for a user's avatar (for screen reader / braille display only).
         * {{name}} is the user's name.
         * For example (when the user focus on Bob's avatar image): Bob profile avatar
         */
        profileAvatar: "{{name}} profile avatar",
        /** A label for a user indicating the user is inactive */
        inactive: "inactive",
        /** A label for a user indicating the user is online */
        online: "online",
        /** A label for a user indicating the user is offline */
        offline: "offline",
        /** A label for a user indicating the user is using a mobile device */
        mobile: "mobile",
        /** A label for a user indicating the user is in a call */
        inCall: "in call",
        /** A label for a user indicating the user is in a meeting */
        inMeeting: "in meeting",
        /** A label for a user indicating the user is out of office */
        outOfOffice: "out of office",
        /** A label for a user indicating the user does not want to be disturbed */
        doNotDisturb: "do not disturb",
        /** A label for a user indicating the user is in a calendar event */
        inACalendarEvent: "in a calendar event",
        /** A label for a user indicating the user is away */
        away: "away",
        /** A label for a user indicating the user is blocking all messages */
        block: "block",
        /** A label for a user indicating the user is busy */
        busy: "busy",
    },
    ImageCropper: {
        /** Label for crop area of an image cropper */
        cropArea: "reposition cropping frame border",
        /** Instructions for moving the crop area */
        moveInstruction: "Use arrow keys to move",
        /** Label for top left corner controller of an image cropper */
        topLeftCorner: "top left corner cropper",
        /** Label for top right corner controller of an image cropper */
        topRightCorner: "top right corner cropper",
        /** Label for bottom left corner controller of an image cropper */
        bottomLeftCorner: "bottom left corner cropper",
        /** Label for bottom right corner controller of an image cropper */
        bottomRightCorner: "bottom right corner cropper",
        /** Label for top edge controller of an image cropper */
        topEdge: "top border cropper",
        /** Label for bottom edge controller of an image cropper */
        bottomEdge: "bottom border cropper",
        /** Label for left edge controller of an image cropper */
        leftEdge: "left border cropper",
        /** Label for right edge controller of an image cropper */
        rightEdge: "right border cropper",
        /** Instructions for resizing the crop area */
        resizeInstruction: "Use arrow keys to resize",
        /**
         * Label for the left cropping value
         * {{percentage}} is the percentage to the left edge to be cropped.
         * For example (when cropping 20% of image to the left): 20% from left edge
         */
        leftPercentage: "{{percentage}} from left edge",
        /**
         * Label for the top cropping value
         * {{percentage}} is the percentage to the top edge to be cropped.
         * For example (when cropping 20% of image to the top): 20% from top edge
         */
        topPercentage: "{{percentage}} from top edge",
        /**
         * Label for the right cropping value
         * {{percentage}} is the percentage to the right edge to be cropped.
         * For example (when cropping 20% of image to the right): 20% from right edge
         */
        rightPercentage: "{{percentage}} from right edge",
        /**
         * Label for the bottom cropping value
         * {{percentage}} is the percentage to the bottom edge to be cropped.
         * For example (when cropping 20% of image to the bottom): 20% from bottom edge
         */
        bottomPercentage: "{{percentage}} from bottom edge",
    },
    Input: {
        /**
         * The cue when there is a prefix before an input box (for screen reader / braille display only).
         * {{prefix}} is the content of the prefix.
         * For example (when the user focused on a text input box for an URL): Prefix is “https://”.
         */
        prefix: "Prefix is “{{prefix}}”.",
        /**
         * The cue when there is a suffix after an input box (for screen reader / braille display only).
         * {{suffix}} is the content of the suffix.
         * For example (when the user focused on a number input box who's unit is kilogram): Suffix is “kg”.
         */
        suffix: "Suffix is “{{suffix}}”.",
        /**
         * The cue when there are both prefix and suffix around an input box (for screen reader / braille display only).
         * {{prefix}} is the content of the prefix.
         * {{suffix}} is the content of the suffix.
         * For example (when the user focused on a text input box for an URL ends with ".com"): Prefix is “https://” and suffix is “.com”.
         */
        prefixAndSuffix: "Prefix is “{{prefix}}” and suffix is “{{suffix}}”.",
        /**
         * The cue for the max length of an input box when the user reached the limit (for screen reader / braille display only).
         * {{maxLength}} is the max characters the input box accepts.
         * For example (when the user tries to type more than 10 characters in a text box only accept 10 characters most): 10 character limit reached.
         */
        maxLengthMessage: "{{maxLength}} character limit reached.",
        /** Click to show (reveal) the password in a password input box */
        showPassword: "Show password",
        /** Click to hide (conceal) the password in a password input box */
        hidePassword: "Hide password",
        /** Click to clear the content inside an input box */
        clear: "Clear",
    },
    InputNumber: {
        /** Click to increase the value of a number input */
        increaseValue: "Increase value",
        /** Click to decrease the value of a number input */
        decreaseValue: "Decrease value",
        /** Default separator for a decimal number */
        decimalSeparator: ".",
    },
    Pagination: {
        /**
         * The text indicating a page number.
         * {{page}} is the number of current page.
         * For example: Page 2
         */
        pageNumber: "Page {{page}}",
        /**
         * The text indicating how many items per page are shown.
         * {{items}} is the number of items shown on one page.
         * For example: 20 per page
         */
        perPage: "{{items}} per page",
        /**
         * The text indicating current and total pages.
         * {{page}} is the number of current page.
         * {{total}} is the number of total page.
         * For example: Page 1 of 5
         */
        pageIndex: "Page {{page}} of {{total}}",
        /** The text indicating how many items are in a list in total.
         * {{total}} is the number of current results.
         * For example: 32 results
         */
        results: "{{total}} results",
        /** The label for the page size selector */
        pageSizeLabel: "Page size",
        /** Click to go to the first page */
        firstPage: "First page",
        /** Click to go to the last page */
        lastPage: "Last page",
        /** Click to go to the previous page */
        previousPage: "Previous page",
        /** Click to go to the next page */
        nextPage: "Next page",
        /** The cue for more pages omitted before current page (for screen reader / braille display only) */
        morePreviousPage: "More pages before are omitted.",
        /** The cue for more pages omitted after current page (for screen reader / braille display only) */
        moreNextPage: "More pages after are omitted.",
    },
    Select: {
        /** The label for an empty option list */
        noData: "No options",
        /** Click to clear all selected items */
        clear: "Clear",
    },
    Stepper: {
        /** The label for a step which is finished */
        finished: "Finished",
        /** The label for a step which is in progress */
        inProgress: "In Progress",
        /** The label for a step which has not started yet */
        notStarted: "Not started",
        /** The label for a step which has error status */
        error: "Error"
    },
    Table: {
        /** The label for an empty table */
        emptyText: "No data",
        /**
         * The label for a table cell with expandable options (for screen reader / braille display only).
         * {{rowIndex}} is the number of current row.
         * {{header}} is the current column header name.
         * {{cellValue}} is the value of current cell.
         * For example: More options about Row 2, User name: Alice.
         */
        expand: "More options about Row {{rowIndex}}, {{header}}: {{cellValue}}",
        /**
         * The label for a table cell inside a draggable row (for screen reader / braille display only).
         * {{rowIndex}} is the number of current row.
         * {{header}} is the current column header name.
         * {{cellValue}} is the value of current cell.
         * For example: Change order of Row 3, Age: 24.
         */
        dragRow: "Change order of Row {{rowIndex}}, {{header}}: {{cellValue}}",
        /**
         * The label for sorting a table by a specified column (for screen reader / braille display only)
         * {{header}} is the current column header name.
         */
        sortLabel: "Sort {{header}}",
        /**
         * The state for a column of table, which is currently sorted in descending order (for screen reader / braille display only)
         * {{header}} is the current column header name.
         */
        sortInDesc: "Sort {{header}} in descending order",
        /**
         * The state for a column of table, which is currently sorted in ascending order (for screen reader / braille display only)
         * {{header}} is the current column header name.
         */
        sortInAsc: "Sort {{header}} in ascending order",
        /** Click to sort by a specified column in descending order */
        triggerDesc: "Click to sort descending",
        /** Click to sort by a specified column in ascending order */
        triggerAsc: "Click to sort ascending",
        /** Click to cancel sorting of a specified column */
        cancelSort: "Click to cancel sorting",
        /** Click to select all rows in a table */
        toggleSelectAll: "Select all",
        /**
         * The label for a table cell, in which user can click to select the row (for screen reader / braille display only).
         * {{rowIndex}} is the number of current row.
         * {{header}} is the current column header name.
         * {{cellValue}} is the value of current cell.
         * For example: Select Row 3, User name: Bob.
         */
        toggleSelectRow: "Select Row {{rowIndex}}, {{header}}: {{cellValue}}",
        /**
         * The label for a table cell, in which user can click to select the row, where only one selection is acceptable (for screen reader / braille display only).
         * {{rowIndex}} is the number of current row.
         * {{header}} is the current column header name.
         * {{cellValue}} is the value of current cell.
         * For example: Select Row 3, User name: Bob (2 of 5).
         */
        toggleSingleSelect: "Select Row {{rowIndex}}, {{header}}: {{cellValue}}",
        /** Click to show all hidden columns of a table */
        showAll: "Show all",
        /** Click to reset all columns of a table to default settings */
        reset: "Reset",
        /** Click to confirm column settings of a table */
        confirm: "Confirm",
        /** The title of column settings of a table */
        columnSetting: "Column setting",
        /** The placeholder for blank cells of a table */
        blank: "blank",
    },
    Tag: {
        /**
         * Click to remove a label from a list.
         * {{label}} is the content of the label.
         * For example (in a color palette): Remove “Red”
         */
        remove: "Remove “{{label}}”",
    },
    Breadcrumbs: {
        /** Click to view more items in a truncated breadcrumb menu list */
        expand: "View more breadcrumbs",
        /** The cue for a list being a breadcrumb navigation menu (for screen reader / braille display only) */
        navLabel: "Breadcrumb",
    },
    Empty: {
        /** The label when there is no results in the page */
        noResults: "No results",
    },
    ColorPicker: {
        /** Click to confirm an action */
        ok: "Ok",
        /** Click to add new color */
        add: "Add",
        /** Click to cancel an action */
        cancel: "Cancel",
        /** Click to edit an action */
        edit: "Edit",
        /** Name a color in a palette */
        nameLabel: "Name this color",
        /** Placeholder of a color's name */
        namePlaceholder: "Color name",
        /** A transparent color */
        transparent: "Transparent",
        /** The name of color white */
        white: "White",
        /** The name of color light gray */
        lightGray: "Light gray",
        /** The name of color gray */
        gray: "Gray",
        /** The name of color dark gray */
        darkGray: "Dark gray",
        /** The name of color black */
        black: "Black",
        /** The name of color light red */
        lightRed: "Light red",
        /** The name of color light orange */
        lightOrange: "Light orange",
        /** The name of color light yellow */
        lightYellow: "Light yellow",
        /** The name of color light green */
        lightGreen: "Light green",
        /** The name of color light blue */
        lightBlue: "Light blue",
        /** The name of color light purple */
        lightPurple: "Light purple",
        /** The name of color red */
        red: "Red",
        /** The name of color orange */
        orange: "Orange",
        /** The name of color yellow */
        yellow: "Yellow",
        /** The name of color green */
        green: "Green",
        /** The name of color blue */
        blue: "Blue",
        /** The name of color purple */
        purple: "Purple",
        /** The name of color dark red */
        darkRed: "Dark red",
        /** The name of color dark orange */
        darkOrange: "Dark orange",
        /** The name of color dark yellow */
        darkYellow: "Dark yellow",
        /** The name of color dark green */
        darkGreen: "Dark green",
        /** The name of color dark blue */
        darkBlue: "Dark blue",
        /** The name of color dark purple */
        darkPurple: "Dark purple",
        /** The title of the button to add color  */
        addColor: "Add color",
        /** The tip message of the tooltip for the Input of color name */
        colorNameTip: "Give your color swatch a name for those who are using a screen reader.",
        /** The tip message of the label for learning more about information */
        learnMore: "Learn more about information",
    },
    DatePicker: {
        /** Weekday names, separated by the comma (","), and start from Sunday */
        weekdayNames: "Sunday,Monday,Tuesday,Wednesday,Thursday,Friday,Saturday",
        /** Weekday names in short date format, separated by the comma (","), and start from Sunday */
        weekdayNamesShort: "Sun,Mon,Tue,Wed,Thu,Fri,Sat",
        /** (Commonly one-letter) weekday abbreviations used as the header of a calendar, separated by the comma (","), and start from Sunday */
        weekdayNamesAbbr: "S,M,T,W,T,F,S",
        /** Month names, separated by the comma (","), and start from January */
        monthNames: "January,February,March,April,May,June,July,August,September,October,November,December",
        /** Month names in short date format, separated by the comma (","), and start from January */
        monthNamesShort: "Jan,Feb,Mar,Apr,May,Jun,Jul,Aug,Sep,Oct,Nov,Dec",
        /** The date format for presenting a specified year */
        yearFormat: "yyyy",
        /** The date format for presenting a specified year and month */
        yearMonthFormat: "MMMM yyyy",
        /** The date format for a specified year and month, in separated part */
        yearMonthFormatSeparated: "{{month}} {{year}}",
        /** The date format for presenting a specified date */
        dateFormat: "MM/dd/yyyy",
        /** Medium date format for presenting a specified date */
        mediumDateFormat: "MMM dd yyyy",
        /** Long date format for presenting a specified date */
        longDateFormat: "dddd MMM dd yyyy",
        /** Click to navigate to the previous month */
        previousMonth: "Previous month",
        /** Click to navigate to the next month */
        nextMonth: "Next month",
        /** Click to navigate to the previous year */
        previousYear: "Previous year",
        /** Click to navigate to the next year */
        nextYear: "Next year",
        /** Click to navigate to the previous decade */
        previousDecade: "Previous decade",
        /** Click to navigate to the next decade */
        nextDecade: "Next decade",
        /** Cue for a date that is selected (for screen reader / braille display only) */
        selected: "selected",
        /** Cue for a date that is the start date of user's selection (for screen reader / braille display only) */
        startDate: "start date",
        /** Cue for a date that is the end date of user's selection (for screen reader / braille display only) */
        endDate: "end date",
        /** Cue for a date that is in the user's selection range (for screen reader / braille display only) */
        inSelection: "in selection range",
        /** Default placeholder copy for the input of DatePicker  */
        selectDate: "Select date",
        /** Default placeholder copy for the start date input of DateTimePicker  */
        selectDateTime: "Select date and time",
        /** Default placeholder copy for the start date input of MonthPicker  */
        selectMonth: "Select month",
        /** Default placeholder copy for the start date input of YearPicker  */
        selectYear: "Select year",
        /** Default placeholder copy for the start date input of DateRangePicker  */
        selectStartDate: "Start date",
        /** Default placeholder copy for the end date input of DateRangePicker  */
        selectEndDate: "End date",
        /** The am and pm label in 12-hour time formats, separated by a comma (",") */
        amPm: "am,pm",
        /** The default time format */
        timeFormat: "hh:mm A",
        /** The default date and time format */
        dateTimeFormat: "MM/dd/yyyy hh:mm A",
        /**
         * The cue for selected hour part of a time (for screen reader / braille display only).
         * {{number}} is the user-selected hour of a time.
         * For example (when the user select the "8" of time 8:05:30): Hour 8
         */
        hourOrder: "Hour {{number}}",
        /**
         * The cue for selected minute part of a time (for screen reader / braille display only).
         * {{number}} is the user-selected minute of a time.
         * For example (when the user select the "05" of time 8:05:30): Minute 5
         */
        minuteOrder: "Minute {{number}}",
        /** The cue for selected second part of a time (for screen reader / braille display only).
         * {{number}} is the user-selected second of a time.
         * For example (when the user select the "30" of time 8:05:30): Second 30
         */
        secondOrder: "Second {{number}}",
        /** The first day of week in a calendar.
         * Must be a number from 0 to 6. 0 stands for Sunday, 1 stands for Monday and so on.
         */
        firstDayOfWeek: "0",
        /** Click to clear the content inside an input box */
        clear: "Clear",
    },
    EmojiPicker: {
        /** label for the recently used emojis */
        recent: "Frequently used",
        /** label for the emojis of People category */
        categoryPeople: "Smileys & People",
        /** label for the emojis of Nature category */
        categoryNature: "Animals & Nature",
        /** label for the emojis of Foods category */
        categoryFoods: "Food & Drink",
        /** label for the emojis of Activity category */
        categoryActivity: "Activity",
        /** label for the emojis of Places category */
        categoryPlaces: "Travel & Places",
        /** label for the emojis of Objects category */
        categoryObjects: "Objects",
        /** label for the emojis of Symbols category */
        categorySymbols: "Symbols",
        /** label for the emojis of Flags category */
        categoryFlags: "Flags",
        /** the placeholder for the search text box */
        search: "Search",
        /** label for the search results of emojis */
        searchResults: "Search results",
        /** label for skin tone of emoji */
        skinTone: "Skin tone",
    },
    TimePicker: {
        /** The am and pm label in 12-hour time formats, separated by a comma (",") */
        amPm: "am,pm",
        /** The default time format */
        timeFormat: "hh:mm A",
        /** The abbreviation for the hour part of a time, used in the header of a selector */
        hourAbbr: "h",
        /** The abbreviation for the minute part of a time, used in the header of a selector */
        minuteAbbr: "min",
        /** The abbreviation for the second part of a time, used in the header of a selector */
        secondAbbr: "s",
        /** The label for the hour list */
        hour: "Hour",
        /** The label for the minute list */
        minute: "Minute",
        /** The label for the second list */
        second: "Second",
        /** The label for the period (AM/PM) list */
        period: "Period",
        /**
         * The cue for selected hour part of a time (for screen reader / braille display only).
         * {{number}} is the user-selected hour of a time.
         * For example (when the user select the "8" of time 8:05:30): Hour 8
         */
        hourOrder: "Hour {{number}}",
        /**
         * The cue for selected minute part of a time (for screen reader / braille display only).
         * {{number}} is the user-selected minute of a time.
         * For example (when the user select the "05" of time 8:05:30): Minute 5
         */
        minuteOrder: "Minute {{number}}",
        /** The cue for selected second part of a time (for screen reader / braille display only).
         * {{number}} is the user-selected second of a time.
         * For example (when the user select the "30" of time 8:05:30): Second 30
         */
        secondOrder: "Second {{number}}",
        /** Default placeholder copy for the input of TimePicker  */
        selectTime: "Select time",
    },
    VideoClipper: {
        /** Label for the media play button */
        play: "Play",
        /** Label for the media pause button */
        pause: "Pause",
        /** Role of a video progress bar, indicating the control is draggable to seek to a time of the video. */
        progressBar: "progress bar",
        /**
         * The label for a media progress bar.
         * {{current}} is the current time.
         * {{duration}} is the total duration of the media.
         * For example (when a 3-minute media plays to minute 1): 1:00 of 3:00
         */
        progressLabel: "{{current}} of {{duration}}",
        /** The tip for a keyboard user for a playhead for video clippers with single segment. */
        singlePlayheadDescription: "press Space to play from the section",
        /** The tip for a keyboard user for a playhead for video clippers with multiple segments. */
        multiplePlayheadDescription: "press Enter to add a new section, press Space to play from the nearest section",
        /**
         * The tip when a user trying to add a clip section when there is already one.
         * {{start}} is the start time of current clip section.
         * {{end}} is the end time of current clip section.
         * For example (when the user tries to add a clip section at 5:00): There is already clip here at 4:00 to 6:00
         */
        existingSection: "There is already clip here at {{start}} to {{end}}",
        /** The label for a list of video clip sections. */
        clipSections: "clip sections",
        /** The label for a video clip section. */
        clipSection: "clip section",
        /**
         * The label for a video clip section in a list.
         * {{order}} is the order number of the section.
         * For example (when the user selected the second section in a video section list): section 2
         */
        clipSectionOrder: "section {{order}}",
        /** The label for the leading edge (start point) of a video segment. User can drag this control to adjust the start time of a video clip. */
        leadingEdge: "start position",
        /** The label for the trailing edge (end point) of a video segment. User can drag this control to adjust the end time of a video clip. */
        trailingEdge: "end position",
        /** Label for a button to remove a section from the video section list. */
        removeSection: "remove section",
        /** Click to trim a video. */
        trim: "Trim",
        /**
         * The cue for a new section of video was added at a specified time on the video track.
         * {{timecode}} is the mm:ss format time code, for the time of video where the section is added.
         * For example (when the user add a video section at one minute of the track): new section added at 01:00
         */
        sectionAdded: "new section added at {{timecode}}",
    },
    Coachmark: {
        /** Click to show the next step of tutorial */
        next: "Next",
        /** Click to show the previous step of tutorial */
        previous: "Previous",
        /** Click to finish and close the tutorial */
        finish: "Finish",
        /**
         * Indicator for the current step.
         * {{current}} is the order of current step.
         * {{total}} is the number of steps in total.
         * For example: step 2 of 4
         */
        indicatorAriaLabel: "step {{current}} of {{total}}",
    },
    Carousel: {
        /** Click to switch to the previous carousel page */
        prevSlide: "Previous carousel page",
        /** Click to switch to the next carousel page */
        nextSlide: "Next carousel page",
        /** Click to start auto scrolling of the carousel */
        play: "Start auto scroll",
        /** Click to pause auto scrolling of the carousel */
        pause: "Pause auto scroll",
        /** The cue for a list being a carousel list (for screen reader / braille display only) */
        carouselRole: "carousel list",
        /**
         * The cue for the total item count and current item of a carousel (for screen reader / braille display only).
         * {{number}} is the order of current item.
         * {{total}} is the number of items in total.
         * For example: 2 of 4.
         */
        itemOrder: "{{number}} of {{total}}.",
        /** The cue for arrow key instruction of keyboard navigation (for screen reader / braille display only) */
        navigateViaArrowKeys: "Press arrow keys to move.",
        /** The cue for tab key instruction of keyboard navigation (for screen reader / braille display only) */
        interactiveElementsAvailable: "Press Tab for more interactive elements.",
        /**
         * Click to go to a specified page of a carousel (for screen reader / braille display only).
         * {{number}} is the number of page.
         * For example: Page 2
         */
        slideOrder: "Page {{number}}",
        /** The cue for the total page count and current page of a carousel (for screen reader / braille display only).
         * {{number}} is the order of current page.
         * {{total}} is the number of pages in total.
         * For example: Page 2 of 4
         */
        slideOrderWithTotal: "Page {{number}} of {{total}}",
    },
    FileUploader: {
        /** The title indicating user can upload a file */
        title: "File upload",
        /** The description of allowed file size and types to upload */
        description: "Max file size is 50MB. Supported file types are JPG, PNG, Video, and PDF.",
        /** Click to upload the file */
        uploadButton: "Upload",
        /** Instructions drag-and-drop a file or press the button to choose a file to upload.
         * {{chooseFileButton}} is the button to choose a file for the alternative way to upload.
         * For example: Drag and drop files or [Choose files]
         */
        dragDescription: "Drag and drop files or {{chooseFileButton}}",
        /** Click to choose a file to upload */
        chooseFile: "Choose files",
        /** Error message when the user uploaded an unaccepted type of file */
        acceptErrorMessage: "Unaccepted type.",
        /** Error message when the user uploaded an oversized file */
        limitSizeErrorMessage: "File exceeds size limit.",
        /** Error message when the user tries to upload multiple files while only one is accepted */
        multipleErrorMessage: "You can't select multiple files to upload.",
        /** Error message when the user tries to upload files more than we can accept */
        maxCountErrorMessage: "Exceeded maximum count of files",
        /** Remove the file with the specified file name
         * {{fileName}} is the full name of the file to be removed
         * For example: Remove file helloworld.jpg
         */
        removeFile: "Remove file {{fileName}}",
    },
    Tabs: {
        /** Press to switch to the previous tab page */
        previous: "Scroll to previous page of tabs",
        /** Press to switch to the next tab page */
        next: "Scroll to next page of tabs",
        /** scrolled the tab list to the previous */
        scrolledPrevious: "Scrolled to previous page",
        /** scrolled the tab list to the next */
        scrolledNext: "Scrolled to next page"
    },
    Form: {
        /** Label for an optional form field */
        optional: "Optional",
        /** button with tooltip for learn more information with complex label(label is not string) */
        learnMore: "Learn more",
        /** button with tooltip for learn more information with label */
        learnMoreWithName: "Learn more about {{name}}"
    },
    MediaControls: {
        /** Label for the media play button */
        play: "Play",
        /** Label for the media pause button */
        pause: "Pause",
        /** Label for the media volume button */
        volume: "Volume",
        /** Label for the media playback rate button */
        rate: "Rate",
    },
    VideoControls: {
        /** Label for the video full screen button */
        fullScreen: "Full screen",
    },
};
